import { useState } from 'react'
import { createPortal } from 'react-dom'
import { useNavigate } from 'react-router-dom'
import { Download, CheckCircle, Share, X, ChevronRight } from 'lucide-react'
import { useGamePWA } from '../../hooks/usePWA'
import { 
  detectPlatform, 
  detectBrowser,
  supportsPWAInstall, 
  downloadShortcut, 
  getDownloadInfo,
  getMobileInstructions,
  getGameUrl
} from '../../utils/platformDownload'

/**
 * Smart Download Button - Works on all platforms
 * - Chrome/Edge: PWA install
 * - Safari Mac: Add to Dock instructions
 * - Safari iOS: Add to Home Screen instructions
 * - Windows: .url shortcut download
 * - Linux: .desktop file download
 * - Android: Add to Home screen instructions
 */
export function DownloadButton({ game, variant = 'default' }) {
  const [showMobileModal, setShowMobileModal] = useState(false)
  const [downloadStatus, setDownloadStatus] = useState(null) // 'success' | 'error' | null
  
  const platform = detectPlatform()
  const browser = detectBrowser()
  const pwaSupported = supportsPWAInstall()
  const downloadInfo = getDownloadInfo(game)
  
  // Use PWA hook for Chrome/Edge
  const {
    isInstallable,
    isInstalled,
    installGame
  } = useGamePWA(game?.id)

  const handleDownload = async () => {
    // For PWA-capable browsers (Chrome/Edge) with install ready, use PWA
    if (pwaSupported && isInstallable) {
      const result = await installGame()
      if (result.success) {
        setDownloadStatus('success')
        return
      }
    }
    
    // Windows/Linux - download shortcut file directly
    if (platform === 'windows' || platform === 'linux') {
      const result = downloadShortcut(game)
      if (result.success) {
        setDownloadStatus('success')
        setTimeout(() => setDownloadStatus(null), 3000)
      }
      return
    }
    
    // Mac/iOS/Android - show instructions modal
    setShowMobileModal(true)
  }

  // Already installed via PWA
  if (isInstalled) {
    return (
      <div className={getContainerClass(variant, 'success')}>
        <CheckCircle className="w-5 h-5" />
        <span className="font-bold">Installed!</span>
      </div>
    )
  }

  // Download success feedback
  if (downloadStatus === 'success') {
    return (
      <div className={getContainerClass(variant, 'success')}>
        <CheckCircle className="w-5 h-5" />
        <span className="font-bold">Downloaded!</span>
      </div>
    )
  }

  // Get button icon based on platform
  const ButtonIcon = (downloadInfo.method === 'ios-instructions' || downloadInfo.method === 'mac-instructions') ? Share : Download

  return (
    <>
      <button
        onClick={handleDownload}
        className={getButtonClass(variant)}
      >
        <ButtonIcon className="w-5 h-5" />
        <span>{downloadInfo.buttonText}</span>
      </button>

      {/* Mobile Instructions Modal */}
      {showMobileModal && (
        <MobileInstructionsModal
          platform={platform}
          game={game}
          onClose={() => setShowMobileModal(false)}
        />
      )}
    </>
  )
}

/**
 * Mobile Instructions Modal
 */
function MobileInstructionsModal({ platform, game, onClose }) {
  const instructions = getMobileInstructions(platform)
  const gameUrl = getGameUrl(game.id)
  
  if (!instructions) return null

  const handleOpenGame = () => {
    window.location.href = gameUrl
  }

  const handleDownloadShortcut = () => {
    const result = downloadShortcut(game)
    if (result.success) {
      onClose()
    }
  }

  const modalContent = (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-6 bg-black/80 backdrop-blur-sm" onClick={onClose}>
      <div 
        className="bg-gray-900 border border-white/10 rounded-2xl w-full max-w-md flex flex-col"
        style={{ maxHeight: 'calc(100vh - 48px)' }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-white/10 shrink-0">
          <h2 className="text-lg font-bold text-white">{instructions.title}</h2>
          <button 
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-white/10 text-white/60 hover:text-white transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        
        {/* Scrollable Content */}
        <div className="overflow-y-auto flex-1">
          {/* Game Info */}
          <div className="p-4 border-b border-white/10 bg-white/5">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-game-primary/20 flex items-center justify-center">
                <svg viewBox="0 0 50 50" className="w-6 h-6">
                  <path 
                    d="M25 0.055c-4.227 0 -8.21 1.057 -11.696 2.91l21.79 7.019c8.573 2.752 13.446 11.909 10.741 20.497 -2.263 7.169 -9.03 11.672 -16.191 11.404 -2.295 -0.087 -3.864 -2.374 -3.155 -4.566 0.481 -1.498 1.901 -2.476 3.47 -2.421 4.101 0.134 7.973 -2.476 9.227 -6.609 1.498 -4.897 -1.325 -10.071 -6.199 -11.64L6.68 8.084C2.571 12.524 0.055 18.47 0.055 25c0 7.831 3.612 14.819 9.259 19.393L16.325 22.587c0.591 -1.838 2.563 -2.847 4.401 -2.256s2.847 2.563 2.256 4.401l-7.5 23.328c2.934 1.207 6.144 1.885 9.519 1.885 13.778 0 24.945 -11.167 24.945 -24.945S38.778 0.055 25 0.055" 
                    fill="#e3ff04"
                  />
                </svg>
              </div>
              <div>
                <h3 className="font-bold text-white">{game.title}</h3>
                <p className="text-sm text-white/60">Polytoria Game</p>
              </div>
            </div>
          </div>
          
          {/* Steps */}
          <div className="p-4 space-y-3">
            {instructions.steps.map((step, index) => (
              <div key={index} className="flex items-start gap-3">
                <div className="w-6 h-6 rounded-full bg-game-primary/20 flex items-center justify-center shrink-0 mt-0.5">
                  <span className="text-xs font-bold text-game-primary">{index + 1}</span>
                </div>
                <p className="text-white/80 text-sm">{step}</p>
              </div>
            ))}
          </div>
          
          {/* Note */}
          {instructions.note && (
            <div className="px-4 pb-4">
              <p className="text-xs text-white/40 italic">{instructions.note}</p>
            </div>
          )}
        </div>
        
        {/* Actions - Always visible at bottom */}
        <div className="p-4 border-t border-white/10 shrink-0">
          {(platform === 'mac' || platform === 'ios' || platform === 'android') ? (
            <button
              onClick={handleOpenGame}
              className="w-full flex items-center justify-center gap-2 py-3 px-4 bg-game-primary text-black font-bold rounded-xl hover:bg-game-primary/90 transition-colors"
            >
              <span>Open Game to Install</span>
              <ChevronRight className="w-4 h-4" />
            </button>
          ) : (
            <button
              onClick={handleDownloadShortcut}
              className="w-full flex items-center justify-center gap-2 py-3 px-4 bg-game-primary text-black font-bold rounded-xl hover:bg-game-primary/90 transition-colors"
            >
              <Download className="w-4 h-4" />
              <span>Download Shortcut</span>
            </button>
          )}
        </div>
      </div>
    </div>
  )
  
  return createPortal(modalContent, document.body)
}

/**
 * Download Banner - Floating prompt for game pages
 */
export function DownloadBanner({ game }) {
  const [isDismissed, setIsDismissed] = useState(false)
  const [showMobileModal, setShowMobileModal] = useState(false)
  const navigate = useNavigate()
  
  const platform = detectPlatform()
  const pwaSupported = supportsPWAInstall()
  const { isInstallable, isInstalled, installGame } = useGamePWA(game?.id)

  if (isDismissed || isInstalled) return null
  
  const handleDownload = async () => {
    if (pwaSupported && isInstallable) {
      await installGame()
      return
    }
    
    if (platform === 'ios' || platform === 'android') {
      setShowMobileModal(true)
      return
    }
    
    downloadShortcut(game)
  }

  return (
    <>
      <div className="fixed bottom-4 left-4 right-4 md:left-auto md:right-4 md:w-96 z-50 animate-slide-up">
        <div className="bg-gradient-to-r from-gray-900 to-gray-800 border border-white/10 rounded-2xl p-4 shadow-2xl">
          <button 
            onClick={() => setIsDismissed(true)}
            className="absolute top-2 right-2 p-1 rounded-full hover:bg-white/10 text-white/40 hover:text-white/80 transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
          <div className="flex items-start gap-3 pr-6">
            <div className="w-12 h-12 rounded-xl bg-game-primary/20 flex items-center justify-center shrink-0">
              <Download className="w-6 h-6 text-game-primary" />
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="font-bold text-white truncate">Download {game?.title}</h3>
              <p className="text-sm text-white/60 mt-0.5">Play anytime from your desktop or home screen</p>
            </div>
          </div>
          <div className="flex gap-2 mt-3">
            <button
              onClick={handleDownload}
              className="flex-1 py-2.5 px-4 bg-game-primary text-black font-bold rounded-xl hover:bg-game-primary/90 transition-colors"
            >
              Download
            </button>
          </div>
        </div>
      </div>

      {showMobileModal && (
        <MobileInstructionsModal
          platform={platform}
          game={game}
          onClose={() => setShowMobileModal(false)}
          onPlayOnline={() => navigate(`/standalone/${game.id}`)}
        />
      )}
    </>
  )
}

// Helper functions for styling
function getContainerClass(variant, state) {
  const baseClass = 'flex items-center justify-center gap-2'
  const stateClass = state === 'success' 
    ? 'text-green-500 bg-green-500/10 border border-green-500/30'
    : 'text-white/60 bg-white/5 border border-white/10'
  const sizeClass = variant === 'large' 
    ? 'w-full py-4 rounded-xl' 
    : 'px-4 py-2 rounded-lg'
  return `${baseClass} ${stateClass} ${sizeClass}`
}

function getButtonClass(variant) {
  const base = 'flex items-center justify-center gap-3 bg-gradient-to-r from-game-primary to-game-primary/80 text-black font-bold hover:scale-[1.02] hover:shadow-lg hover:shadow-game-primary/30 transition-all cursor-pointer'
  return variant === 'large' 
    ? `${base} w-full py-4 rounded-xl text-base` 
    : `${base} px-4 py-2 rounded-lg`
}
